import type { IndexLiveSpec, IndexQuote, LiveSpec, Quote } from '@/lib/market/types'

import { OPEN_MINUTE, SESSION_MINUTES } from '@/lib/market/calendar'
import { createRng, hashString, mixSeed, normal } from '@/lib/market/random'

export interface BookLevel {
    price: number
    size: number
    orders: number
    total: number
}

export interface OrderBook {
    bids: BookLevel[]
    asks: BookLevel[]
    spread: number
    mid: number
    imbalance: number
}

export interface TradePrint {
    id: string
    time: string
    price: number
    size: number
    side: 'buy' | 'sell'
}

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value))

export const tickSize = (price: number): number => (price >= 100 ? 0.25 : 0.01)

export const roundToTick = (price: number): number => {
    const tick = tickSize(price)

    return Number((Math.round(price / tick) * tick).toFixed(2))
}

export const volumeDensity = (progress: number): number => {
    const u = 2 * progress - 1

    return 0.6 + 2.4 * u * u
}

export const volumeShare = (progress: number): number => {
    const x = clamp01(progress)
    const u = 2 * x - 1

    return (0.6 * x + (2.4 * (u * u * u + 1)) / 6) / 1.4
}

export const effectiveProgress = (spec: { cap?: number }, progress: number): number => clamp01(Math.min(progress, spec.cap ?? 1))

export const quoteAt = (spec: LiveSpec, progress: number): Quote => {
    const path = spec.path
    const steps = path.length - 1
    const p = effectiveProgress(spec, progress)
    const position = p * steps
    const index = Math.min(steps, Math.floor(position))
    const fraction = position - index
    const current = index >= steps
        ? path[steps]
        : roundToTick(path[index] + (path[index + 1] - path[index]) * fraction)

    let high = current
    let low = current
    let weighted = 0
    let weights = 0

    for (let step = 0; step <= index; step++) {
        const price = path[step]
        if (price > high) high = price
        if (price < low) low = price

        const weight = volumeDensity(step / steps)
        weighted += price * weight
        weights += weight
    }

    if (index < steps) {
        const weight = volumeDensity(p) * fraction
        weighted += current * weight
        weights += weight
    }

    const share = volumeShare(p)
    const volume = Math.round(spec.volume * share)
    const deals = Math.round(spec.deals * share)
    const vwap = weights > 0 ? weighted / weights : current
    const change = Number((current - spec.prevClose).toFixed(2))

    return {
        last: current,
        prevClose: spec.prevClose,
        open: spec.open,
        high,
        low,
        change,
        changePct: spec.prevClose > 0 ? ((current - spec.prevClose) / spec.prevClose) * 100 : 0,
        volume,
        turnover: Math.round(volume * vwap),
        deals,
        vwap: Number(vwap.toFixed(4))
    }
}

export const indexQuoteAt = (spec: IndexLiveSpec, progress: number): IndexQuote => {
    const path = spec.path
    const steps = path.length - 1
    const p = effectiveProgress(spec, progress)
    const position = p * steps
    const index = Math.min(steps, Math.floor(position))
    const fraction = position - index
    const raw = index >= steps ? path[steps] : path[index] + (path[index + 1] - path[index]) * fraction
    const level = Number(raw.toFixed(2))

    let high = level
    let low = level
    for (let step = 0; step <= index; step++) {
        if (path[step] > high) high = path[step]
        if (path[step] < low) low = path[step]
    }

    return {
        level,
        prevClose: spec.prevClose,
        open: spec.open,
        change: Number((level - spec.prevClose).toFixed(2)),
        changePct: spec.prevClose > 0 ? ((level - spec.prevClose) / spec.prevClose) * 100 : 0,
        high,
        low
    }
}

export const sessionPathUntil = (path: number[], progress: number): number[] => {
    const steps = path.length - 1
    const p = clamp01(progress)
    const position = p * steps
    const index = Math.min(steps, Math.floor(position))
    const points = path.slice(0, index + 1)

    if (index < steps) {
        const fraction = position - index
        if (fraction > 0) points.push(path[index] + (path[index + 1] - path[index]) * fraction)
    }

    return points
}

export const buildOrderBook = (ticker: string, quote: Quote, averageVolume: number, seedKey: number, levels = 10): OrderBook => {
    const rng = createRng(mixSeed(hashString(`${ticker}:book`), seedKey))
    const tick = tickSize(quote.last)
    const liquidity = Math.max(1, averageVolume)
    const thin = liquidity < 20000
    const spreadTicks = thin ? 2 + Math.floor(rng() * 4) : 1 + Math.floor(rng() * 2)
    const bestBid = roundToTick(quote.last - tick * Math.floor(spreadTicks / 2))
    const bestAsk = roundToTick(Math.max(bestBid + tick, quote.last + tick * Math.ceil(spreadTicks / 2)))
    const baseSize = Math.max(50, liquidity / 140)
    const averageTicket = Math.max(20, Math.round(45000 / Math.max(0.5, quote.last)))

    const side = (start: number, direction: 1 | -1): BookLevel[] => {
        const rows: BookLevel[] = []
        let price = start
        let total = 0

        for (let level = 0; level < levels; level++) {
            if (level > 0) {
                const gap = thin ? 1 + Math.floor(rng() * 3) : rng() < 0.8 ? 1 : 2
                price = roundToTick(price + direction * tick * gap)
            }

            if (price <= 0) break

            const size = Math.max(1, Math.round(baseSize * Math.exp(0.65 * normal(rng)) * (1 + level * 0.18)))
            const orders = Math.max(1, Math.min(size, Math.round(size / averageTicket + rng() * 2)))
            total += size
            rows.push({ price, size, orders, total })
        }

        return rows
    }

    const bids = side(bestBid, -1)
    const asks = side(bestAsk, 1)
    const bidDepth = bids.reduce((acc, row) => acc + row.size, 0)
    const askDepth = asks.reduce((acc, row) => acc + row.size, 0)

    return {
        bids,
        asks,
        spread: Number((bestAsk - bestBid).toFixed(2)),
        mid: Number(((bestAsk + bestBid) / 2).toFixed(3)),
        imbalance: bidDepth + askDepth > 0 ? (bidDepth - askDepth) / (bidDepth + askDepth) : 0
    }
}

const pad = (value: number): string => (value < 10 ? `0${value}` : `${value}`)

const sessionClock = (secondsIntoSession: number): string => {
    const total = OPEN_MINUTE * 60 + Math.floor(secondsIntoSession)
    const hours = Math.floor(total / 3600)
    const minutes = Math.floor((total % 3600) / 60)
    const seconds = total % 60

    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
}

export const buildRecentTrades = (ticker: string, day: number, spec: LiveSpec, progress: number, count = 14): TradePrint[] => {
    const p = effectiveProgress(spec, progress)
    if (p <= 0 || spec.deals <= 0) return []

    const sessionSeconds = SESSION_MINUTES * 60
    const elapsed = p * sessionSeconds
    const rng = createRng(mixSeed(hashString(`${ticker}:tape`), day))
    const averageGap = sessionSeconds / Math.max(1, spec.deals)
    const averageSize = Math.max(1, spec.volume / Math.max(1, spec.deals))
    const steps = spec.path.length - 1
    const buffer: TradePrint[] = []
    let time = 0
    let previousPrice = spec.open
    let sequence = 0
    let guard = 0

    while (guard < 200000) {
        guard++

        const gap = -Math.log(Math.max(1e-9, rng())) * averageGap
        time += gap
        if (time > elapsed) break

        const position = (time / sessionSeconds) * steps
        const index = Math.min(steps - 1, Math.floor(position))
        const fraction = position - index
        const base = spec.path[index] + (spec.path[index + 1] - spec.path[index]) * fraction
        const tick = tickSize(base)
        const jitter = rng() < 0.5 ? 0 : (rng() < 0.5 ? -1 : 1) * tick
        const price = roundToTick(base + jitter)
        const size = Math.max(1, Math.round(averageSize * Math.exp(0.9 * normal(rng) - 0.4)))
        const side: 'buy' | 'sell' = price > previousPrice ? 'buy' : price < previousPrice ? 'sell' : rng() < 0.5 ? 'buy' : 'sell'

        previousPrice = price
        sequence++
        buffer.push({ id: `${ticker}-${day}-${sequence}`, time: sessionClock(time), price, size, side })
        if (buffer.length > count) buffer.shift()
    }

    return buffer.reverse()
}
